import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getCsrfToken } from '@ctct/ctct-helpers';
import { trackAction } from '@ctct/distui-helpers';
import { Button } from '@ctct/ui-react';

import UpgradeStepContent from './UpgradeStepContent';
import { distUiGoTo } from '../api/DistUI';
import MyAccountApi from '../api/MyAccountApi';
import PriceEstimatorDialog from '../PriceEstimatorDialog';
import { useUpgradeContext } from '../UpgradeContext';
import { stepsEnum } from '../utils';

const { payForPlan } = MyAccountApi;

const UpgradeStep = ({ footerContent, footerActions, hasScheduledDowngrade }) => {
  const { t } = useTranslation();
  const [upgradeContext, dispatchUpgrade] = useUpgradeContext();
  const [noPaymentForm, setNoPaymentForm] = useState(false);
  const [doPayForPlan, setDoPayForPlan] = useState(false);
  const [showEstimatorDialog, setShowEstimatorDialog] = useState(false);
  const [needsPlansAndPricing, setNeedsPlansAndPricing] = useState(null);
  const {
    isTwoStepDialog,
    upgradeRating,
    upgradeInfo,
    upgradeBillingInfo,
    paymentSuccessCallback,
    upgradeFormattedCustomerInfo,
    unsavedChangesInCallingPage,
    wrapClassName,
    upgradeReturnURL,
    upgradeReturnLabel,
    selectedPlanIdFromCompare,
    payingForPlan,
    recommendedUpgradePlanPromotion,
    isStripeAccount,
    stripeToken,
  } = upgradeContext;
  const { paymentPlanDiscountPeriod } = upgradeInfo || {};

  const termsAndConditionsKey = useMemo(() => {
    let key = `upgrade-popup.terms-and-conditions${recommendedUpgradePlanPromotion ? '-lg' : ''}`;

    if (paymentPlanDiscountPeriod === 6) {
      key += '-6';
    } else if (paymentPlanDiscountPeriod === 12) {
      key += '-12';
    }

    return key;
  }, [paymentPlanDiscountPeriod]);

  useEffect(() => {
    return () => {
      // reset on close
      if (selectedPlanIdFromCompare) {
        dispatchUpgrade({ type: 'setSelectedPlanIdFromCompare', value: null });
      }
    };
  }, []);

  useEffect(() => {
    if (doPayForPlan && upgradeFormattedCustomerInfo) {
      (async () => {
        const leaveUpgradeStep = () => {
          setDoPayForPlan(false);
          dispatchUpgrade({ type: 'setPayingForPlan', value: false });
        };

        const handleFailure = () => {
          paymentSuccessCallback(false);
          leaveUpgradeStep();
          trackAction({
            action: 'Feature Upgrade Dialog, upgrade failure',
          });
          dispatchUpgrade({ type: 'setUpgradeStep', value: stepsEnum.FAILURE });
        };
        const { stripeQuoteId } = upgradeRating || {};

        let paymentType = {
          displayString: 'onfile',
          name: 'ONFILE',
        };

        if (noPaymentForm) {
          paymentType = {
            displayString: 'NO_PAY',
            name: 'NO_PAY',
          };
        }

        if (isStripeAccount) {
          paymentType = {
            name: 'STRIPE',
            displayString: 'Stripe',
          };
        }

        const payload = {
          ratingData: upgradeRating,
          customer: upgradeFormattedCustomerInfo,
          payment: {
            paymentType,
            ...(isStripeAccount ? { stripe: { token: stripeToken } } : null),
          },
          ...(stripeQuoteId ? { stripeQuoteId } : null),
        };

        try {
          trackAction({
            action: 'Feature Upgrade Dialog, starting upgrade',
          });
          dispatchUpgrade({ type: 'setPayingForPlan', value: true });

          const response = await payForPlan(payload);

          if (response.status === 200) {
            leaveUpgradeStep();
            paymentSuccessCallback(true);
            trackAction({
              action: 'Feature Upgrade Dialog, upgrade success',
            });
            dispatchUpgrade({ type: 'setUpgradeStep', value: stepsEnum.SUCCESS });
          } else {
            handleFailure();
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(e);
          handleFailure();
        }
      })();
    }
  }, [doPayForPlan, upgradeFormattedCustomerInfo]);

  useEffect(() => {
    if (upgradeBillingInfo && upgradeRating) {
      const { amountToCharge } = upgradeRating;

      const {
        payment_info: { payment_type: paymentType },
      } = upgradeBillingInfo;
      // for stripe accounts, paymentType will be null, but it does not been that payment method is not on file
      const noPaymentFormLocal = (!paymentType || paymentType === 'check') && !isStripeAccount;

      setNoPaymentForm(noPaymentFormLocal);

      if (noPaymentFormLocal && amountToCharge >= 0) setNeedsPlansAndPricing(true);
      else setNeedsPlansAndPricing(false);
    }
  }, [isStripeAccount, upgradeBillingInfo, upgradeRating]);

  const primaryButtonClick = () => {
    if (noPaymentForm) {
      const params = {
        ctoken: getCsrfToken(),
        returnURL: upgradeReturnURL || (window && window.location && window.location.href) ? window.location.href : '',
      };

      trackAction({
        action: 'Feature Upgrade Dialog, proceeding to billingInformation',
      });
      distUiGoTo('billingInformation', params);
    } else {
      trackAction({
        action: 'Feature Upgrade Dialog, proceeding to payForServices',
      });
      distUiGoTo('payForServices', { returnURL: upgradeReturnURL, returnLabel: upgradeReturnLabel });
    }
  };

  if (!footerContent && !footerActions) {
    return <UpgradeStepContent />;
  }

  // footerContent
  if (!footerActions) {
    return (
      <div className={`agreement-text ${isTwoStepDialog ? '' : 'single-step-footer'}`}>
        <Trans i18nKey={termsAndConditionsKey} t={t}>
          <strong />
          <Button
            data-qe-id="see-pricing-tiers-link"
            className="see-pricing-tiers"
            style={{ verticalAlign: 'bottom' }}
            onClick={() => {
              setShowEstimatorDialog(true);
            }}
            kind="link"
            text={t('upgrade-popup.see-pricing-tiers-link')}
          />
        </Trans>
        {showEstimatorDialog && (
          <PriceEstimatorDialog wrapClassName={wrapClassName} onClose={() => setShowEstimatorDialog(false)} />
        )}
      </div>
    );
  }

  // footerActions
  return (
    <>
      {isTwoStepDialog && <span className="step">{t('upgrade-popup.step-2')}</span>}
      <Button
        shape="pill"
        data-qe-id="primary-upgrade-button"
        text={(() => {
          if (needsPlansAndPricing) {
            if (noPaymentForm) {
              return t('upgrade-popup.button-text.add-payment-method');
            }

            return t('upgrade-popup.button-text.view-pricing');
          }

          return t('upgrade-popup.button-text.upgrade-now');
        })()}
        kind="primary"
        busy={payingForPlan}
        disabled={hasScheduledDowngrade || needsPlansAndPricing == null}
        onClick={() => {
          if (needsPlansAndPricing) {
            if (unsavedChangesInCallingPage) {
              dispatchUpgrade({ type: 'setConfirmProceedCallback', value: primaryButtonClick });
            } else primaryButtonClick();

            return;
          }

          setDoPayForPlan(true);
        }}
      />
    </>
  );
};

UpgradeStep.defaultProps = {
  footerContent: false,
  footerActions: false,
  hasScheduledDowngrade: false,
};

UpgradeStep.propTypes = {
  footerContent: PropTypes.bool,
  footerActions: PropTypes.bool,
  hasScheduledDowngrade: PropTypes.bool,
};

export default UpgradeStep;
